import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { getAuthToken } from '../utils/api';
import { foodProfiles } from '../data/foodProfiles';
import { recipes } from '../data/recipes';
import { supplements } from '../data/supplements';
import { lifestyleHabits } from '../data/lifestyleHabits';

const NOTE_TYPES = [
  { value: 'learning', label: 'Learning / Insight' },
  { value: 'question', label: 'Question for Doctor' },
  { value: 'reflection', label: 'Personal Reflection' },
  { value: 'revisit', label: 'Something to Revisit' }
];

const NoteModal = ({ note, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    note_type: 'learning',
    linked_context: []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // All available items for linking
  const allLinkableItems = [
    ...foodProfiles.map(f => ({ ...f, category: 'Food' })),
    ...recipes.map(r => ({ ...r, category: 'Recipe' })),
    ...supplements.map(s => ({ ...s, category: 'Supplement' })),
    ...lifestyleHabits.map(l => ({ ...l, category: 'Lifestyle' }))
  ];

  // Initialize form with existing note data if editing
  useEffect(() => {
    if (note) {
      setFormData({
        title: note.title || '',
        content: note.content || '',
        note_type: note.note_type || 'learning',
        linked_context: note.linked_context || []
      });
    }
  }, [note]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.content.trim()) {
      setError('Note content is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const url = note
        ? `${backendUrl}/api/notes/${note.id}`
        : `${backendUrl}/api/notes`;
      
      const method = note ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save note');
      }
      
      // Close modal and trigger refresh
      onClose(true);
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkedContextToggle = (itemId) => {
    setFormData(prev => ({
      ...prev,
      linked_context: prev.linked_context.includes(itemId)
        ? prev.linked_context.filter(id => id !== itemId)
        : [...prev.linked_context, itemId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">
            {note ? 'Edit Note' : 'New Note'}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-slate-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Title (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Title (Optional)
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Add a title for your note..."
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Note Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Note Type *
            </label>
            <select
              value={formData.note_type}
              onChange={(e) => setFormData({ ...formData, note_type: e.target.value })}
              required
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {NOTE_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Note Content *
            </label>
            
            {/* Optional Reflection Prompts */}
            <details className="mb-3 bg-purple-50 border border-purple-200 rounded-xl p-4">
              <summary className="text-sm font-medium text-purple-900 cursor-pointer hover:text-purple-700 transition-colors">
                💭 Need inspiration? Try these optional reflection prompts
              </summary>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <p className="italic text-slate-600 text-xs mb-3">
                  These are just ideas to get you started — use them if they help, or write whatever feels right to you.
                </p>
                <ul className="space-y-1.5 pl-4">
                  <li className="list-disc">What stood out to me this week?</li>
                  <li className="list-disc">What felt confusing or unclear?</li>
                  <li className="list-disc">What would I like to ask at my next appointment?</li>
                  <li className="list-disc">What helped me feel calmer or more in control?</li>
                  <li className="list-disc">What did I learn recently about my health or CLL?</li>
                  <li className="list-disc">What patterns am I noticing over time?</li>
                  <li className="list-disc">What am I curious about right now?</li>
                  <li className="list-disc">What would I like to explore or read more about?</li>
                  <li className="list-disc">How am I feeling today, and why might that be?</li>
                  <li className="list-disc">What do I want to remember or revisit later?</li>
                </ul>
              </div>
            </details>
            
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your note here..."
              required
              rows={8}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Linked Context (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Link to Items (Optional)
            </label>
            <p className="text-sm text-slate-600 mb-3">
              Select items from Integrative Health that relate to this note.
            </p>
            <div className="border border-slate-200 rounded-xl p-4 max-h-64 overflow-y-auto">
              {allLinkableItems.length === 0 ? (
                <p className="text-sm text-slate-500">No items available to link.</p>
              ) : (
                <div className="space-y-2">
                  {allLinkableItems.map(item => (
                    <label
                      key={item.id}
                      className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.linked_context.includes(item.id)}
                        onChange={() => handleLinkedContextToggle(item.id)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">
                        {item.name}
                        <span className="text-slate-500 ml-2">({item.category})</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : note ? 'Update Note' : 'Create Note'}
            </button>
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NoteModal;
